import { Card, Input, Space, Switch, Table, Tag, Typography, type TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { useStockListBatches, useStockStock } from '@/api/generated/endpoints/stock/stock';
import type { BatchOut, StockRow } from '@/api/generated/model';
import { ExpiryTag } from '@/components/ExpiryTag';
import { QtyCell } from '@/components/qty';
import { useIsMobile } from '@/lib/useIsMobile';
import { useTableState } from '@/lib/useTableState';
import { uomLabel } from '@/lib/units';
import { BatchDrawer } from '@/pages/stock/BatchDrawer';

const DATE = 'DD.MM.YYYY';

/** Остатки по товарам; раскрытие строки — партии в порядке отгрузки (FEFO). */
export function StockPage() {
  const { t } = useTranslation();
  const table = useTableState<'q' | 'empty'>({ filters: ['q', 'empty'] });
  const [search, setSearch] = useState(table.filters.q[0] ?? '');
  // ?batch=12 — открыть карточку сразу: так приходят по QR этикетки палета.
  const [params, setParams] = useSearchParams();
  const [batchId, setBatchId] = useState<number | null>(() => Number(params.get('batch')) || null);
  const includeEmpty = table.filters.empty[0] === '1';
  const mobile = useIsMobile();
  const list = useStockStock({
    limit: table.query.limit,
    offset: table.query.offset,
    q: table.filters.q[0] ?? null,
    include_empty: includeEmpty,
  });

  // Телефон (T-176): товар, остаток и ближайший срок — в одной ячейке, без широких колонок.
  const mobileColumns: TableColumnsType<StockRow> = [
    {
      key: 'product',
      title: t('stock.colProduct'),
      render: (_: unknown, r) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong>{r.product.name}</Typography.Text>
          <Space size={4} wrap>
            <QtyCell value={r.qty_available} uom={r.product.base_uom} />
            {r.nearest_expiry && <ExpiryTag expiresOn={r.nearest_expiry} />}
            {r.has_undated && <Tag>{t('stock.undated')}</Tag>}
          </Space>
        </Space>
      ),
    },
  ];

  const columns: TableColumnsType<StockRow> = [
    {
      key: 'product',
      title: t('stock.colProduct'),
      render: (_: unknown, r) => (
        <Space>
          {r.product.name}
          <Tag>{uomLabel(t, r.product.base_uom)}</Tag>
        </Space>
      ),
    },
    {
      key: 'qty',
      title: t('stock.colQty'),
      width: 160,
      align: 'right',
      render: (_: unknown, r) => <QtyCell value={r.qty_available} uom={r.product.base_uom} />,
    },
    { key: 'batches', title: t('stock.colBatches'), dataIndex: 'batches', width: 90 },
    {
      key: 'nearest',
      title: t('stock.colNearest'),
      width: 280,
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          {r.nearest_expiry && <ExpiryTag expiresOn={r.nearest_expiry} />}
          {r.has_undated && <Tag>{t('stock.undated')}</Tag>}
        </Space>
      ),
    },
  ];

  return (
    <Card title={t('stock.title')}>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder={t('stock.searchPlaceholder')}
          style={{ width: mobile ? '100%' : 260 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (e.target.value === '') table.setFilter('q', null);
          }}
          onSearch={(q) => {
            table.setFilter('q', q.trim() || null);
          }}
        />
        <Space>
          <Switch
            size="small"
            checked={includeEmpty}
            onChange={(on) => {
              table.setFilter('empty', on ? '1' : null);
            }}
          />
          {t('stock.includeEmpty')}
        </Space>
      </Space>
      <Table<StockRow>
        rowKey={(r) => r.product.id}
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data?.items}
        loading={list.isFetching}
        pagination={table.pagination(list.data?.total)}
        onChange={table.onTableChange}
        expandable={{
          expandedRowRender: (r) => <ProductBatches productId={r.product.id} onOpen={setBatchId} />,
          rowExpandable: (r) => r.batches > 0,
        }}
      />
      <BatchDrawer
        fullWidth={mobile}
        batchId={batchId}
        pallet={Number(params.get('pallet')) || null}
        onClose={() => {
          setBatchId(null);
          if (params.has('batch')) {
            params.delete('batch');
            params.delete('pallet');
            setParams(params, { replace: true });
          }
        }}
      />
    </Card>
  );
}

function ProductBatches({
  productId,
  onOpen,
}: {
  productId: number;
  onOpen: (batchId: number) => void;
}) {
  const { t } = useTranslation();
  // Порядок FEFO: срок по возрастанию; партии без срока PostgreSQL ставит в конец (ASC NULLS LAST).
  const list = useStockListBatches({
    product_id: productId,
    status: 'in_stock',
    sort: 'expires_on',
    limit: 200,
  });

  const mobile = useIsMobile();
  const mobileColumns: TableColumnsType<BatchOut> = [
    {
      key: 'batch',
      title: t('batches.colBatch'),
      render: (_: unknown, b) => (
        <Space direction="vertical" size={2}>
          <Space size={4} wrap>
            <a>№{b.id}</a>
            <ExpiryTag expiresOn={b.expires_on} days={b.days_left} />
          </Space>
          <QtyCell value={b.qty_available} uom={b.product.base_uom} />
        </Space>
      ),
    },
  ];

  const columns: TableColumnsType<BatchOut> = [
    {
      key: 'id',
      title: t('batches.colBatch'),
      dataIndex: 'id',
      width: 90,
      render: (id: number) => <a>№{id}</a>,
    },
    {
      key: 'received',
      title: t('batches.colReceived'),
      dataIndex: 'received_on',
      width: 110,
      render: (d: string) => dayjs(d).format(DATE),
    },
    {
      key: 'expires',
      title: t('batches.colExpires'),
      width: 260,
      render: (_: unknown, b) => <ExpiryTag expiresOn={b.expires_on} days={b.days_left} />,
    },
    {
      key: 'available',
      title: t('batches.colAvailable'),
      align: 'right',
      width: 130,
      render: (_: unknown, b) => <QtyCell value={b.qty_available} uom={b.product.base_uom} />,
    },
    {
      key: 'received_qty',
      title: t('batches.colReceivedQty'),
      align: 'right',
      width: 130,
      render: (_: unknown, b) => <QtyCell value={b.qty_received} uom={b.product.base_uom} />,
    },
    {
      key: 'origin',
      title: t('batches.colOrigin'),
      render: (_: unknown, b) => [b.country, b.caliber].filter(Boolean).join(', ') || '—',
    },
    {
      key: 'supplier',
      title: t('batches.colSupplier'),
      dataIndex: 'supplier',
      render: (s: string | null) => s ?? '—',
    },
  ];

  return (
    <>
      <Typography.Text type="secondary">{t('stock.fefoHint')}</Typography.Text>
      <Table<BatchOut>
        rowKey="id"
        size="small"
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data?.items}
        loading={list.isFetching}
        pagination={false}
        locale={{ emptyText: t('batches.empty') }}
        onRow={(b) => ({
          onClick: () => {
            onOpen(b.id);
          },
          style: { cursor: 'pointer' },
        })}
      />
    </>
  );
}
